// ABOUTME: OAuth callback handler for calendar provider authorization flows.
// ABOUTME: Extracts authorization code from URL, completes the connection, and redirects to My Family page.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { calendarAPI } from '../services/api';
import { CalendarProviderType } from '../types/api';

export default function CalendarOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`Authorization was denied: ${errorParam}`);
        setTimeout(() => navigate('/my-family'), 3000);
        return;
      }

      if (!code) {
        setError('No authorization code received.');
        setTimeout(() => navigate('/my-family'), 3000);
        return;
      }

      // Verify state matches what we stored
      const storedState = sessionStorage.getItem('calendar_oauth_state');
      const storedProvider = sessionStorage.getItem('calendar_oauth_provider');

      if (!storedState || state !== storedState) {
        setError('Invalid state parameter. Please try again.');
        setTimeout(() => navigate('/my-family'), 3000);
        return;
      }

      // Clean up session storage
      sessionStorage.removeItem('calendar_oauth_state');
      sessionStorage.removeItem('calendar_oauth_provider');
      sessionStorage.removeItem('calendar_oauth_reconnect_id');

      try {
        const providerType = Number(storedProvider) as CalendarProviderType;
        const redirectUri = `${window.location.origin}/calendar/oauth/callback`;

        await calendarAPI.createConnection({
          providerType,
          authorizationCode: code,
          redirectUri,
        });

        // Redirect to My Family page with success
        navigate('/my-family', { state: { calendarConnected: true } });
      } catch {
        setError('Failed to complete calendar connection. Please try again.');
        setTimeout(() => navigate('/my-family'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Connecting your calendar...
          </Typography>
        </>
      )}
    </Box>
  );
}
